import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  ListItemIcon,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Language as LanguageIcon,
  Palette as PaletteIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
} from "@mui/icons-material";
import { useTokenVerification } from "../../hooks/useTokenVerification";
import { useThemeContext } from "../../config/ThemeProvider";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import AvatarMenu from "../AvatarMenu";
import Cookies from "js-cookie";

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { isInitialized } = useTokenVerification();
  const { t, i18n } = useTranslation();
  const { paletteType, togglePalette } = useThemeContext();
  const { updatePaletteType } = useUserPreferences();
  const isAuthenticated = !!Cookies.get("token");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  const handleHomeClick = () => {
    navigate("/");
  };

  const handleSignInClick = () => {
    navigate("/auth/signin");
  };

  const handleSignUpClick = () => {
    navigate("/auth/signup");
  };

  const handleCreateHabitClick = () => {
    navigate("/habits/create");
  };

  const handlePricingClick = () => {
    navigate("/pricing");
  };

  const handleUpgradePlanClick = () => {
    navigate("/upgrade-plan");
  };

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
  };

  const handlePaletteToggle = () => {
    // Toggle la palette dans le contexte local immédiatement
    togglePalette();

    // Sauvegarder la nouvelle palette dans les préférences utilisateur si l'utilisateur est connecté
    if (isAuthenticated) {
      const newPaletteType = paletteType === "warm" ? "dark" : "light";
      updatePaletteType(newPaletteType);
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleNavigateAndCloseDrawer = (path: string) => {
    navigate(path);
    handleDrawerClose();
  };

  return (
    <AppBar position="sticky">
      <Toolbar>
        <Typography
          variant="h6"
          component={Link}
          to="/"
          sx={{
            flexGrow: 1,
            cursor: "pointer",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          Kuse
        </Typography>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!isAuthenticated && (
              <Button color="inherit" onClick={handleHomeClick}>
                {t("navbar.home")}
              </Button>
            )}
            {isAuthenticated ? (
              <>
                <Button color="inherit" onClick={() => navigate("/habits")}>
                  {t("navbar.myHabits")}
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigate("/streaks")}
                  startIcon={<LocalFireDepartmentIcon />}
                >
                  {t("navbar.streaks")}
                </Button>
                <Button color="inherit" onClick={handleCreateHabitClick}>
                  {t("navbar.createHabit")}
                </Button>
                <Button color="inherit" onClick={handleUpgradePlanClick}>
                  {t("navbar.upgradePlan")}
                </Button>
                <AvatarMenu isAuthenticated={isAuthenticated} />
              </>
            ) : (
              <>
                <Button color="inherit" onClick={handlePricingClick}>
                  {t("navbar.pricing")}
                </Button>
                <Button color="inherit" onClick={handleSignInClick}>
                  {t("navbar.signIn")}
                </Button>
                <Button color="inherit" onClick={handleSignUpClick}>
                  {t("navbar.signUp")}
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Mobile Navigation */}
        {isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AvatarMenu isAuthenticated={isAuthenticated} />
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        {/* Mobile Drawer */}
        <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerClose}>
          <Box
            sx={{ width: 250 }}
            role="presentation"
            onClick={handleDrawerClose}
            onKeyDown={handleDrawerClose}
          >
            <List>
              {!isAuthenticated && (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavigateAndCloseDrawer("/")}
                  >
                    <ListItemText primary={t("navbar.home")} />
                  </ListItemButton>
                </ListItem>
              )}

              {isAuthenticated ? (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigateAndCloseDrawer("/habits")}
                    >
                      <ListItemText primary={t("navbar.myHabits")} />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigateAndCloseDrawer("/streaks")}
                    >
                      <ListItemIcon>
                        <LocalFireDepartmentIcon />
                      </ListItemIcon>
                      <ListItemText primary={t("navbar.streaks")} />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleNavigateAndCloseDrawer("/habits/create")
                      }
                    >
                      <ListItemText primary={t("navbar.createHabit")} />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleNavigateAndCloseDrawer("/upgrade-plan")
                      }
                    >
                      <ListItemText primary={t("navbar.upgradePlan")} />
                    </ListItemButton>
                  </ListItem>
                </>
              ) : (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigateAndCloseDrawer("/pricing")}
                    >
                      <ListItemText primary={t("navbar.pricing")} />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleNavigateAndCloseDrawer("/auth/signin")
                      }
                    >
                      <ListItemText primary={t("navbar.signIn")} />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() =>
                        handleNavigateAndCloseDrawer("/auth/signup")
                      }
                    >
                      <ListItemText primary={t("navbar.signUp")} />
                    </ListItemButton>
                  </ListItem>
                </>
              )}

              {/* Language selector for mobile when not authenticated */}
              {!isAuthenticated && (
                <>
                  <Divider />
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleLanguageChange("fr")}>
                      <ListItemIcon>
                        <LanguageIcon />
                      </ListItemIcon>
                      <ListItemText primary="Français" />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding>
                    <ListItemButton onClick={() => handleLanguageChange("en")}>
                      <ListItemIcon>
                        <LanguageIcon />
                      </ListItemIcon>
                      <ListItemText primary="English" />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                  <ListItem disablePadding>
                    <ListItemButton onClick={handlePaletteToggle}>
                      <ListItemIcon>
                        <PaletteIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          paletteType === "warm"
                            ? "Mode Sombre"
                            : "Mode Chaleureux"
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </>
              )}
            </List>
          </Box>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
